'use client';

import { ITotalData } from '@/api/types/cart';
import Popup from '@/components/Popup';
import { useStore } from '@/providers/app-store-provider';
import { EPlatform } from '@/utils/enums/platform-enum';
import { formatPriceNumberWithSymbol } from '@/utils/price';
import { useTranslations } from '@/i18n/use-translations';

interface OrderSummaryProps {
  show: boolean;
  totalData: ITotalData;
  onClose: () => void;
  deviceType: EPlatform;
  halfPriceData?: {
    status?: number;
    title?: string;
    content?: string;
    rule?: {
      rule_flag?: number;
      discount_rate?: string;
      discount_text?: string;
      threshold_price?: number;
      restrict_single?: number;
    };
  };
}

export function OrderSummary({
  show,
  totalData,
  onClose,
  deviceType,
  halfPriceData,
}: OrderSummaryProps) {
  const t = useTranslations('cart.orderSummary');
  const { currency } = useStore((state) => state);

  const hasDiscount = totalData.discount && totalData.discount !== '0.00';

  if (deviceType !== EPlatform.WAP) {
    return null;
  }

  return (
    <Popup
      open={show}
      onClose={onClose}
      position="bottom"
      title={t('title')}
      height="auto"
      round
      borderRadius={20}
      sx={{
        zIndex: 360, // 遮住顶部导航（z-100, z-201）和公告栏，但低于底部导航（z-1300）和NoticeBar弹层（z-1400）
        '& .MuiDrawer-root': {
          zIndex: 360,
        },
        '& .MuiDrawer-paper': {
          zIndex: 360,
          borderRadius: '20px 20px 0 0',
        },
      }}
    >
      {/* 内容区域 - 添加底部padding为底部导航留出空间 */}
      <div className="pb-[85px]">
        <ul className="space-y-6 mb-4">
          {/* 总价 */}
          <li className="flex items-center justify-between mb-[24px]">
            <span className="text-[14px]">{t('total')}</span>
            <span className="text-[14px] font-bb">
              {formatPriceNumberWithSymbol(totalData.total || '0.00', currency)}
            </span>
          </li>

          {/* 折扣信息 */}
          {hasDiscount && (
            <li className="flex items-center justify-between mb-[24px]">
              <span className="text-[14px]">
                {halfPriceData?.rule?.discount_text || t('discount')}{' '}
                {halfPriceData?.rule?.discount_rate || ''}% {t('off')}
              </span>
              <span className="text-[14px] font-bb">
                -{formatPriceNumberWithSymbol(totalData.discount, currency)}
              </span>
            </li>
          )}

          {/* 小计 */}
          <li className="flex items-center justify-between mb-[16px]">
            <span className="text-[16px] font-bs">{t('subtotal')}</span>
            <span className="text-[16px] font-bb">
              {formatPriceNumberWithSymbol(totalData.subtotal || '0.00', currency)}
            </span>
          </li>
        </ul>

        {/* 底部提示文字 */}
        <div className="mx-auto w-[93vw] h-[32px] px-[6px] rounded-[4px] leading-[32px] text-[12px] text-[#808080] bg-[#f8f9fa]">
          {t('applyCouponHint')}
        </div>
      </div>
    </Popup>
  );
}
